/**
 * Componente para exibir reações em mensagens
 * 
 * Mostra emojis de reação abaixo do balão da mensagem,
 * com posicionamento baseado na direção (inbound/outbound)
 */

import { cn } from '@/lib/utils';

/**
 * Interface para uma reação individual
 */
export interface MessageReaction {
  emoji: string;
  reacted_by: string;  // Telefone de quem reagiu
  reacted_at: number;  // Timestamp
}

interface MessageReactionsProps {
  /** Array de reações da mensagem */
  reactions?: MessageReaction[] | null;
  /** Direção da mensagem para posicionamento */
  direction: 'inbound' | 'outbound';
  className?: string;
}

/**
 * Exibe reações de uma mensagem em formato de badges pequenos
 * Posicionado absolutamente abaixo do balão da mensagem
 */
export function MessageReactions({ 
  reactions, 
  direction,
  className 
}: MessageReactionsProps) {
  // Não renderiza se não houver reações
  if (!reactions || reactions.length === 0) return null;

  // Agrupa reações iguais para contagem (caso múltiplas pessoas reajam igual)
  const groupedReactions = reactions.reduce<Record<string, number>>((acc, reaction) => {
    if (reaction.emoji) {
      acc[reaction.emoji] = (acc[reaction.emoji] || 0) + 1;
    }
    return acc;
  }, {});

  const reactionEntries = Object.entries(groupedReactions);
  
  if (reactionEntries.length === 0) return null;

  return (
    <div
      className={cn(
        'absolute bottom-0 flex gap-0.5 z-10',
        // Posiciona à direita para outbound, esquerda para inbound
        direction === 'outbound' ? 'right-2' : 'left-2',
        className
      )}
    >
      {reactionEntries.map(([emoji, count]) => (
        <span
          key={emoji}
          className={cn(
            'text-sm bg-background rounded-full px-1.5 py-0.5 shadow-sm border',
            'flex items-center gap-0.5'
          )}
          title={`${count} ${count === 1 ? 'reação' : 'reações'}`}
        >
          {emoji}
          {/* Mostra contador apenas se mais de 1 pessoa reagiu */}
          {count > 1 && (
            <span className="text-[10px] text-muted-foreground">{count}</span>
          )}
        </span>
      ))}
    </div>
  );
}
